import React, { useEffect, useState } from 'react';
import { useStore } from '../store/useStore';
import { usePuffApi } from '../hooks/usePuffApi';
import { theme } from '../theme';

export const MoneySaved: React.FC = () => {
  const { user, todayCount } = useStore();
  const { refreshStats } = usePuffApi();
  const [weeklyAvg, setWeeklyAvg] = useState<number | null>(null);
  const [statsLoaded, setStatsLoaded] = useState(false);

  const startingAmount = user?.dailyAmount || 20;
  const costPerPuff = user?.costPerPuff || 0.5;
  const dailyCostBefore = startingAmount * costPerPuff;

  const daysSinceStart = (() => {
    const start = user?.createdAt ? new Date(user.createdAt) : new Date();
    return Math.max(1, Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24)));
  })();

  // Fetch stats
  useEffect(() => {
    if (!user?.id) return;
    refreshStats('month').then(stats => {
      if (stats?.dailyStats) {
        const values = Object.values(stats.dailyStats) as number[];
        if (values.length > 0) {
          const avg = values.reduce((a, b) => a + b, 0) / values.length;
          setWeeklyAvg(Math.round(avg * 10) / 10);
        }
      }
    }).catch(() => { }).finally(() => setStatsLoaded(true));
  }, [user?.id]);

  // Document title
  useEffect(() => {
    document.title = 'เงินที่ประหยัดได้';
    return () => { document.title = 'porddee - ตัวช่วยเลิกสูบ'; };
  }, []);

  // Calculations — allow negative (spending more)
  const avgDaily = weeklyAvg ?? todayCount;
  const dailyCostNow = avgDaily * costPerPuff;
  const diffPerDay = dailyCostBefore - dailyCostNow; // positive = saving, negative = spending more
  const isSaving = diffPerDay > 0;
  const absDiffPerDay = Math.abs(diffPerDay);
  const changePct = dailyCostBefore > 0 ? Math.round((absDiffPerDay / dailyCostBefore) * 100) : 0;

  // Cumulative estimate
  const totalSavedEstimate = isSaving ? Math.round(diffPerDay * daysSinceStart) : 0;
  const totalOverspent = !isSaving ? Math.round(absDiffPerDay * daysSinceStart) : 0;

  // Projections
  const projections = isSaving ? [
    { label: '1 สัปดาห์', amount: Math.round(diffPerDay * 7) },
    { label: '1 เดือน', amount: Math.round(diffPerDay * 30) },
    { label: '6 เดือน', amount: Math.round(diffPerDay * 180) },
    { label: '1 ปี', amount: Math.round(diffPerDay * 365) },
  ] : [];

  // Extra spending projections when overspending
  const overspendProjections = !isSaving && absDiffPerDay > 0 ? [
    { label: '1 สัปดาห์', amount: Math.round(absDiffPerDay * 7) },
    { label: '1 เดือน', amount: Math.round(absDiffPerDay * 30) },
    { label: '6 เดือน', amount: Math.round(absDiffPerDay * 180) },
    { label: '1 ปี', amount: Math.round(absDiffPerDay * 365) },
  ] : [];


  if (!user || !statsLoaded) {
    return (
      <div className="page" style={{ background: '#f8fafc', minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>กำลังโหลด...</div>
      </div>
    );
  }

  return (
    <div className="page" style={{ background: '#f8fafc', height: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <div className="page-content" style={{ padding: 'min(5vw, 20px) min(5vw, 20px) calc(env(safe-area-inset-bottom, 20px) + 120px)' }}>

        <h1 style={{ fontSize: 'clamp(20px, 5vw, 24px)', fontWeight: 800, color: '#0f172a', marginBottom: 'min(5vw, 20px)' }}>
          💰 เงินที่ประหยัดได้
        </h1>

        {/* ── 1. TOTAL SAVED / OVERSPENT CARD ── */}
        <div style={{
          background: isSaving
            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            : `linear-gradient(135deg, ${theme.primaryLight} 0%, ${theme.primary} 100%)`,
          borderRadius: 'min(6vw, 24px)', padding: 'min(6vw, 24px)', color: 'white', marginBottom: 16,
          boxShadow: isSaving
            ? '0 8px 24px rgba(16, 185, 129, 0.25)'
            : `0 8px 24px rgba(${theme.primaryRgb}, 0.3)`,
        }}>
          <div style={{ fontSize: 'clamp(11px, 3vw, 13px)', opacity: 0.85, marginBottom: 4 }}>
            {isSaving ? 'ประหยัดไปแล้วทั้งหมด (ประมาณ)' : 'ค่าใช้จ่ายที่เพิ่มขึ้น (ประมาณ)'}
          </div>
          <div style={{ fontSize: 'clamp(32px, 10vw, 44px)', fontWeight: 800, lineHeight: 1.2, marginBottom: 12 }}>
            {isSaving ? `฿${totalSavedEstimate.toLocaleString()}` : `+฿${totalOverspent.toLocaleString()}`}
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 14px', background: 'rgba(255,255,255,0.2)',
            borderRadius: 20, fontSize: 'clamp(11px, 3vw, 13px)',
          }}>
            ตั้งแต่เริ่มใช้แอป ({daysSinceStart} วัน)
          </div>
        </div>

        {/* ── 2. BEFORE vs AFTER ── */}
        <div style={{
          background: 'white', borderRadius: 'min(5vw, 20px)', padding: 'min(5vw, 20px)', marginBottom: 16,
          border: '2px solid #f1f5f9',
        }}>
          <div style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>
            เปรียบเทียบค่าใช้จ่าย
          </div>
          <div style={{ display: 'flex', gap: 'min(3vw, 10px)', marginBottom: 14 }}>
            {/* Before */}
            <div style={{ flex: 1, background: '#f8fafc', borderRadius: 16, padding: 'min(3vw, 12px) 4px', textAlign: 'center' }}>
              <div style={{ fontSize: 'clamp(9px, 2.5vw, 11px)', color: '#64748b', fontWeight: 600, marginBottom: 4 }}>ก่อนลด</div>
              <div style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 800, color: '#64748b', wordBreak: 'break-word', lineHeight: 1.2 }}>
                ฿{Math.round(dailyCostBefore * 30).toLocaleString()}
              </div>
              <div style={{ fontSize: 'clamp(9px, 2.5vw, 11px)', color: '#94a3b8' }}>/ เดือน</div>
            </div>
            {/* After — dynamic color based on saving or overspending */}
            <div style={{
              flex: 1,
              background: isSaving ? '#ecfdf5' : theme.primarySoft,
              borderRadius: 16, padding: 'min(3vw, 12px) 4px', textAlign: 'center',
            }}>
              <div style={{
                fontSize: 'clamp(9px, 2.5vw, 11px)', fontWeight: 600, marginBottom: 4,
                color: isSaving ? '#10b981' : theme.primary,
              }}>ตอนนี้</div>
              <div style={{
                fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 800,
                color: isSaving ? '#059669' : theme.primary,
                wordBreak: 'break-word', lineHeight: 1.2
              }}>
                ฿{Math.round(dailyCostNow * 30).toLocaleString()}
              </div>
              <div style={{
                fontSize: 'clamp(9px, 2.5vw, 11px)',
                color: isSaving ? '#34d399' : theme.primaryLight,
              }}>/ เดือน</div>
            </div>
          </div>
          {/* Change badge */}
          {changePct > 0 && (
            <div style={{
              textAlign: 'center', padding: '8px 0', borderRadius: 12,
              background: isSaving ? '#f0fdf4' : theme.primarySoft,
            }}>
              <span style={{
                fontSize: 'clamp(12px, 3.2vw, 14px)', fontWeight: 700,
                color: isSaving ? '#059669' : theme.primary,
              }}>
                {isSaving
                  ? `ลดค่าใช้จ่ายได้ ${changePct}% 🎉`
                  : `ค่าใช้จ่ายเพิ่มขึ้น ${changePct}% ⚠️`
                }
              </span>
            </div>
          )}
        </div>

        {/* ── 3. PROJECTIONS (saving) ── */}
        {isSaving && projections.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
              ถ้าลดต่อแบบนี้ จะประหยัดได้
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'min(2.5vw, 10px)' }}>
              {projections.map((p, i) => (
                <div key={i} style={{
                  background: 'white', borderRadius: 16, padding: 'min(3vw, 12px) 4px',
                  border: '2px solid #f1f5f9', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 'clamp(9px, 2.5vw, 11px)', color: '#64748b', marginBottom: 4 }}>{p.label}</div>
                  <div style={{ fontSize: 'clamp(16px, 4.5vw, 22px)', fontWeight: 800, color: '#059669', wordBreak: 'break-word', lineHeight: 1.2 }}>
                    ฿{p.amount.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── 3b. OVERSPEND PROJECTIONS ── */}
        {!isSaving && overspendProjections.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
              ถ้าสูบเท่านี้ต่อ จะจ่ายเพิ่มอีก
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'min(2.5vw, 10px)' }}>
              {overspendProjections.map((p, i) => (
                <div key={i} style={{
                  background: 'white', borderRadius: 16, padding: 'min(3vw, 12px) 4px',
                  border: `2px solid ${theme.primarySoft}`, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 'clamp(9px, 2.5vw, 11px)', color: '#64748b', marginBottom: 4 }}>{p.label}</div>
                  <div style={{ fontSize: 'clamp(16px, 4.5vw, 22px)', fontWeight: 800, color: theme.primary, wordBreak: 'break-word', lineHeight: 1.2 }}>
                    +฿{p.amount.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
